import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.service';
import { AuthStorageService } from '../services/auth-storage.service';
import { SessionVaultService } from '../services/session-vault.service';
import { WsService } from '../../realtime/ws.service';
import { environments } from '../../../environments/environment';
import { isOnPublicAuthRoute, isPasswordRecoveryPath, isPublicApiRequest } from '../utils/auth-route.utils';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<boolean>(false);

function attachAuthHeader(req: HttpRequest<any>, accessToken: string | null): HttpRequest<any> {
  if (!accessToken) {
    return req;
  }

  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`
    }
  });
}

function shouldSuppressLoginRedirect(): boolean {
  return isPasswordRecoveryPath() || isOnPublicAuthRoute();
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const authStorage = inject(AuthStorageService);
  const authService = inject(AuthService);
  const sessionVault = inject(SessionVaultService);
  const wsService = inject(WsService);
  const router = inject(Router);

  const accessToken = sessionVault.getAccessToken();
  const authReq = attachAuthHeader(req, accessToken);
  const isRefreshRequest = req.url.includes('/refresh-token');
  const onPasswordRecoveryPage = isPasswordRecoveryPath();

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const backendMessage = error.error.message || '';
        const isSessionConflict = backendMessage.toLowerCase().includes('another device');

        if (isSessionConflict && !onPasswordRecoveryPage) {
          authService.clearLocalSession();
          Swal.fire({
            icon: 'warning',
            title: 'Session Expired',
            text: 'Your account has been logged in from another device.',
            confirmButtonText: 'OK',
            allowOutsideClick: false
          }).then(() => router.navigate(['/auth/login']));
          return throwError(() => error);
        }

        const isAuthMeRequest = req.url.includes('/auth/me');

        if (
          onPasswordRecoveryPage ||
          isAuthMeRequest ||
          (isPublicApiRequest(req.url, environments.apiUrl) && !isAuthMeRequest) ||
          isRefreshRequest
        ) {
          return throwError(() => error);
        }

        const refreshToken = sessionVault.getRefreshToken();
        if (!refreshToken) {
          authService.clearLocalSession();
          if (!shouldSuppressLoginRedirect()) {
            router.navigate(['/auth/login']);
          }
          return throwError(() => error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(false);

          return authService.refreshToken(refreshToken).pipe(
            switchMap(() => {
              isRefreshing = false;
              refreshTokenSubject.next(true);
              wsService.ensureConnected();
              return next(attachAuthHeader(req, sessionVault.getAccessToken()));
            }),
            catchError(refreshError => {
              isRefreshing = false;
              authService.clearLocalSession();
              if (!shouldSuppressLoginRedirect()) {
                router.navigate(['/auth/login']);
              }
              return throwError(() => refreshError);
            })
          );
        }

        return refreshTokenSubject.pipe(
          filter(success => success),
          take(1),
          switchMap(() => next(attachAuthHeader(req, sessionVault.getAccessToken())))
        );
      }

      if (error.status === 403 && !onPasswordRecoveryPage) {
        const isClientScope = req.url.includes('/client/') || router.url.includes('/client/');
        const isRoleDenied =
          !isClientScope &&
          !isPublicApiRequest(req.url, environments.apiUrl) &&
          !isOnPublicAuthRoute() &&
          !req.url.includes('/subscriptions/');

        if (isRoleDenied && !req.url.includes('/refresh-token')) {
          router.navigate(['/']);
        }
      }

      return throwError(() => error);
    })
  );
};
